import { ExtractDataFunctionReturn, ParsedArgs, PixelData } from '../types';

// Keep in mind that this function will only
// parse the PBM (PNM 4) data without its header
const parsePBMData = (rawImageData: number[], imageWidth: number, imageHeight: number): PixelData[][] => {
  const pixels: PixelData[][] = [];
  let tempRowPixels: PixelData[] = [];

  // Stealed and ported from
  // https://github.com/dbrant/imageformats/blob/master/ImageFormats/PnmReader.cs#L149-L171
  const maxElementCount = imageWidth * imageHeight * 4;
  let x = 0,
    elementCount = 0;

  for (const pixel of rawImageData as number[]) {
    for (let p = 7; p >= 0; p--) {
      const pixelVal = (pixel & (1 << p)) == 0 ? 0 : 255;

      tempRowPixels.push({
        r: pixelVal,
        g: pixelVal,
        b: pixelVal,
      });

      elementCount++;
      if (elementCount >= maxElementCount) break;

      x++;
      if (x >= imageWidth) {
        pixels.push(tempRowPixels);
        tempRowPixels = [];
        x = 0;
        break;
      }
    }

    if (elementCount >= maxElementCount) break;
  }

  return pixels;
};

export const specifyGraphicDataCommand = (parsedArgs: ParsedArgs): ExtractDataFunctionReturn => {
  const [, , , fn] = parsedArgs;
  let extractedImage: PixelData[][] = [];

  switch (fn) {
    case 112: {
      const rawImageData = parsedArgs[parsedArgs.length - 1] as number[];
      const [xL, xH, yL, yH] = parsedArgs.slice(-5) as number[];
      extractedImage = parsePBMData(rawImageData, xL + xH * 256, yL + yH * 256);
      break;
    }

    default:
      return null;
  }

  return {
    extractedData: {
      image: extractedImage,
    },
  };
};

export const printRasterBitImageModeCommand = (parsedArgs: ParsedArgs): ExtractDataFunctionReturn => {
  const rawImageData = parsedArgs[parsedArgs.length - 1] as number[];
  const [xL, xH, yL, yH] = parsedArgs.slice(-5) as number[];
  const extractedImage: PixelData[][] = parsePBMData(rawImageData, (xL + xH * 256) * 8, yL + yH * 256);

  return {
    extractedData: {
      image: extractedImage,
    },
  };
};

export const selectBitImageModeCommand = (parsedArgs: ParsedArgs): ExtractDataFunctionReturn => {
  const [m, nL, nH] = parsedArgs as number[];
  const rawImageData = parsedArgs[parsedArgs.length - 1] as number[];
  const width = nL + nH * 256;
  const height = (() => {
    switch (m) {
      case 0:
      case 1:
        return 8;

      case 32:
      case 33:
        return 24;
    }
  })();

  /**
   * Here we will get a valid PBM, with the image reflected diagonally compared with the original.
   * this is due to the swapped width and height (and we should do it this way since the data is
   * also reflected in the first place)
   */
  const extractedImage: PixelData[][] = parsePBMData(rawImageData, height, width);

  return {
    extractedData: {
      image: extractedImage,
    },
  };
};

export const printBarcodeCommand = (parsedArgs: ParsedArgs): ExtractDataFunctionReturn => {
  const data = parsedArgs[parsedArgs.length - 1] as number[];
  data.pop(); // remove last NULL byte

  const extractedBarcode = data.map((byte) => String.fromCharCode(byte));

  return {
    extractedData: {
      code: extractedBarcode,
    },
  };
};

export const print2dCodesCommand = (parsedArgs: ParsedArgs): ExtractDataFunctionReturn => {
  const [, , cn, fn] = parsedArgs;

  let extracted2dCode = [];
  switch (cn) {
    case 48: {
      // PDF417 Parsing implementation
      if (fn === 80) {
        const data = parsedArgs[parsedArgs.length - 1] as number[];
        extracted2dCode = data.map((byte) => byte.toString(16));
      }
      break;
    }

    case 49: {
      // QR Code Parsing implementation
      if (fn === 80) {
        const data = parsedArgs[parsedArgs.length - 1] as number[];
        extracted2dCode = data.map((byte) => byte.toString(16));
      }
      break;
    }

    default:
      return null;
  }

  return {
    extractedData: {
      code: extracted2dCode,
    },
  };
};
